import React, { useCallback, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import injectSheet from 'react-jss';
import compose from 'lodash.flowright';

import { Icon, FormWriter } from '@stratumn/atomic';

import { uploadConfig } from 'client/media';

import {
  FORMS_BATCH_DELETE,
  FORMS_BATCH_UPDATE,
  FORMS_BATCH_REPORT_VALIDATION_STATUS,
  FORMS_BATCH_TOGGLE_LOCK,
  FORMS_BATCH_TOGGLE_LINK
} from '../reducers';

import styles from './batchForm.style';

export const BatchForm = React.memo(
  ({
    classes,
    workflowContext,
    schema,
    uiSchema,
    id,
    isMainForm,
    isInBatch,
    data,
    linkedPaths,
    lockedPaths,
    updateForms,
    uploadFile,
    validating,
    hasErrorFocus
  }) => {
    // callback to close a form
    const onFormClose = useCallback(() => {
      updateForms({
        type: FORMS_BATCH_DELETE,
        id
      });
    }, [id, updateForms]);

    // callback to update form data
    const onFormDataChange = useCallback(
      args => {
        const { formData } = args;
        updateForms({
          type: FORMS_BATCH_UPDATE,
          id,
          data: formData
        });
      },
      [id, updateForms]
    );

    // ref to a hidden submit button, to trigger form validation manually
    const sumbitBtnRef = useRef(null);
    useEffect(() => {
      // if validating state changes and is now true, trigger form validation manually
      // validation result will be reported thanks to reportSuccess and reportError
      if (validating && sumbitBtnRef.current) sumbitBtnRef.current.click();
    }, [validating]);
    const reportSuccess = useCallback(() => {
      updateForms({
        type: FORMS_BATCH_REPORT_VALIDATION_STATUS,
        id,
        success: true
      });
    }, [id, updateForms]);
    const reportError = useCallback(() => {
      updateForms({
        type: FORMS_BATCH_REPORT_VALIDATION_STATUS,
        id,
        success: false
      });
    }, [id, updateForms]);

    const links = useMemo(
      () => ({
        id,
        isMainForm,
        onClickLock: path => {
          updateForms({
            type: FORMS_BATCH_TOGGLE_LOCK,
            path
          });
        },
        onClickLink: path => {
          updateForms({
            type: FORMS_BATCH_TOGGLE_LINK,
            id,
            path
          });
        },
        linkedPaths,
        lockedPaths
      }),
      [id, isMainForm, linkedPaths, lockedPaths, updateForms]
    );

    const staticUploadConfig = useMemo(
      () => ({
        maxFileSize: uploadConfig.MAX_FILE_SIZE,
        fileExtensionWhitelistMap: uploadConfig.FILE_EXTENSION_WHITELIST_MAP
      }),
      []
    );

    return (
      <div
        className={classes.form}
        data-is-main={isMainForm}
        data-error-focus={hasErrorFocus}
      >
        <div className={classes.formHeader}>
          {isInBatch &&
            (isMainForm ? (
              'Main form'
            ) : (
              <div
                className={classes.closeFormIcon}
                onClick={onFormClose}
                data-cy="close-form"
              >
                <Icon name="Cross" size={24} />
              </div>
            ))}
        </div>
        <FormWriter
          id={id}
          schema={schema}
          uiSchema={uiSchema}
          onChange={onFormDataChange}
          formData={data}
          uploadFile={uploadFile}
          uploadConfig={staticUploadConfig}
          onSubmit={reportSuccess}
          onError={reportError}
          links={links}
          workflowContext={workflowContext}
        />
        {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
        <button
          type="submit"
          className={classes.shadowSubmitBtn}
          form={id}
          ref={sumbitBtnRef}
        />
      </div>
    );
  }
);
BatchForm.propTypes = {
  classes: PropTypes.object.isRequired,
  workflowContext: PropTypes.object.isRequired,
  schema: PropTypes.object.isRequired,
  uiSchema: PropTypes.object.isRequired,
  id: PropTypes.string.isRequired,
  isMainForm: PropTypes.bool,
  isInBatch: PropTypes.bool,
  data: PropTypes.object.isRequired,
  linkedPaths: PropTypes.arrayOf(PropTypes.string),
  lockedPaths: PropTypes.arrayOf(PropTypes.string),
  updateForms: PropTypes.func.isRequired,
  uploadFile: PropTypes.func.isRequired,
  validating: PropTypes.bool.isRequired,
  hasErrorFocus: PropTypes.bool.isRequired
};

BatchForm.defaultProps = {
  isMainForm: false,
  isInBatch: false,
  linkedPaths: [],
  lockedPaths: []
};

export default compose(injectSheet(styles))(BatchForm);
